/**
 * 推荐系统API服务
 * 处理推荐关系和奖励的后端API调用
 */

import { apiClient } from './ApiClient';
import { authApi } from './AuthApiService';
import { logger } from '@utils/logger';

export interface ReferralStats {
  totalReferrals: number;
  activeReferrals: number;
  totalRewards: number;
  recentRewards: number;
}

export interface ReferralInfo {
  id: string;
  username: string;
  displayName: string | null;
  role: string;
  status: string;
  createdAt: string;
  totalRewards: number;
}

export interface ReferralRewardInfo {
  id: string;
  referredId: string;
  referredUsername: string;
  amount: number;
  type: string;
  description: string | null;
  createdAt: string;
}

// 推荐码信息接口
export interface ReferralCodeInfo {
  id: string;
  code: string;
  type: string;
  isActive: boolean;
  usageCount: number;
  description: string | null;
  createdAt: string;
  updatedAt: string;
  expiresAt: string | null;
  // 赠金池相关
  bonusPoolBalance: number;
  userDepositAmount: number;
  platformBonusAmount: number;
  bonusPerUse: number;
  totalBonusGiven: number;
}

// 赠金交易记录接口
export interface BonusTransactionInfo {
  id: string;
  transactionType: string;
  amount: number;
  balanceBefore: number;
  balanceAfter: number;
  fromUserId: string | null;
  toUserId: string | null;
  description: string | null;
  createdAt: string;
}

// 赠金池状态接口
export interface BonusPoolStatus {
  bonusPoolBalance: number;
  userDepositAmount: number;
  platformBonusAmount: number;
  bonusPerUse: number;
  totalBonusGiven: number;
  usageCount: number;
  estimatedRemainingUses: number;
}

class ReferralApiService {
  /**
   * 设置推荐人
   */
  async setReferrer(referrerCode: string): Promise<{ success: boolean; message: string }> {
    logger.info(`Setting referrer: ${referrerCode}`);
    try {
      const token = authApi.getToken();
      if (!token) throw new Error('Not authenticated');
      
      const response = await apiClient.post<{ success: boolean; message: string }>(
        '/api/referral/set-referrer',
        { referrerCode },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (!response || !response.data) {
        throw new Error('设置推荐人失败');
      }
      
      logger.info('Referrer set successfully:', response.data.message);
      return response.data;
    } catch (error: any) {
      logger.error('设置推荐人异常:', error);
      throw error;
    }
  }

  /**
   * 获取推荐人信息
   */
  async getReferrer(): Promise<ReferralInfo | null> {
    logger.info('Fetching referrer info.');
    try {
      const token = authApi.getToken();
      if (!token) throw new Error('Not authenticated');
      
      const response = await apiClient.get<{ referrer?: ReferralInfo | null }>(
        '/api/referral/referrer',
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (!response || !response.data) {
        logger.warn('推荐人响应为空');
        return null;
      }
      
      const referrer = response.data.referrer || null;
      logger.info('Fetched referrer info:', referrer?.username || 'None');
      return referrer;
    } catch (error: any) {
      logger.error('获取推荐人异常:', error);
      return null;
    }
  }

  /**
   * 获取推荐统计
   */
  async getStats(): Promise<ReferralStats> {
    logger.info('Fetching referral stats.');
    try {
      const token = authApi.getToken();
      if (!token) throw new Error('Not authenticated');
      
      const response = await apiClient.get<{ stats?: ReferralStats }>(
        '/api/referral/stats',
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (!response || !response.data || !response.data.stats) {
        logger.warn('推荐统计响应为空，返回默认值');
        return {
          totalReferrals: 0,
          activeReferrals: 0,
          totalRewards: 0,
          recentRewards: 0,
        };
      }
      
      logger.info('Fetched referral stats:', response.data.stats);
      return response.data.stats;
    } catch (error: any) {
      logger.error('获取推荐统计异常:', error);
      return {
        totalReferrals: 0,
        activeReferrals: 0,
        totalRewards: 0,
        recentRewards: 0,
      };
    }
  }

  /**
   * 获取推荐列表
   */
  async getReferralList(): Promise<ReferralInfo[]> {
    logger.info('Fetching referral list.');
    try {
      const token = authApi.getToken();
      if (!token) throw new Error('Not authenticated');
      
      const response = await apiClient.get<{ referrals?: ReferralInfo[] }>(
        '/api/referral/list',
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      // 處理可能的空數據或錯誤響應
      if (!response || !response.data) {
        logger.warn('推荐列表响应为空');
        return [];
      }
      
      const referrals = response.data.referrals || [];
      logger.info(`Fetched ${referrals.length} referrals.`);
      return referrals;
    } catch (error: any) {
      logger.error('获取推荐列表异常:', error);
      return []; // 返回空數組而不是拋出錯誤
    }
  }

  /**
   * 获取奖励记录
   */
  async getRewardHistory(): Promise<ReferralRewardInfo[]> {
    logger.info('Fetching reward history.');
    try {
      const token = authApi.getToken();
      if (!token) throw new Error('Not authenticated');
      
      const response = await apiClient.get<{ rewards?: ReferralRewardInfo[] }>(
        '/api/referral/rewards',
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (!response || !response.data) {
        logger.warn('奖励记录响应为空');
        return [];
      }
      
      const rewards = response.data.rewards || [];
      logger.info(`Fetched ${rewards.length} reward records.`);
      return rewards;
    } catch (error: any) {
      logger.error('获取奖励记录异常:', error);
      return [];
    }
  }

  /**
   * 设置自定义推荐码
   */
  async setCustomReferralCode(customReferralCode: string): Promise<{ success: boolean; message: string; user?: any }> {
    logger.info(`Setting custom referral code: ${customReferralCode}`);
    try {
      const token = authApi.getToken();
      if (!token) throw new Error('Not authenticated');
      
      // 确保 ApiClient 有 token
      apiClient.setToken(token);
      
      const response = await apiClient.post<{ success: boolean; message: string; user?: any; error?: string }>(
        '/api/referral/set-custom-code',
        { customReferralCode }
      );
      
      if (!response || !response.data) {
        throw new Error('设置自定义推荐码失败');
      }
      
      // 检查是否有错误
      if (response.error || response.data.error) {
        const errorMsg = response.error || response.data.error || '设置自定义推荐码失败';
        logger.error('Custom referral code set failed:', errorMsg);
        throw new Error(errorMsg);
      }
      
      logger.info('Custom referral code set successfully:', response.data.message);
      return response.data;
    } catch (error: any) {
      logger.error('设置自定义推荐码异常:', error);
      throw error;
    }
  }

  // ========== 推荐码管理方法（新）==========

  /**
   * 获取用户的所有推荐码
   */
  async getUserReferralCodes(): Promise<ReferralCodeInfo[]> {
    logger.info('Fetching user referral codes.');
    try {
      const token = authApi.getToken();
      if (!token) throw new Error('Not authenticated');
      
      const response = await apiClient.get<{ codes?: ReferralCodeInfo[] }>(
        '/api/referral/codes',
        { token }
      );
      
      const codes = response.data?.codes || [];
      logger.info(`Fetched ${codes.length} referral codes.`);
      return codes;
    } catch (error: any) {
      logger.error('获取推荐码列表异常:', error);
      return [];
    }
  }

  /**
   * 创建新推荐码
   */
  async createReferralCode(
    code: string,
    description?: string,
    expiresAt?: Date
  ): Promise<{ success: boolean; code: ReferralCodeInfo; message: string }> {
    logger.info(`Creating referral code: ${code}`);
    try {
      const token = authApi.getToken();
      if (!token) throw new Error('Not authenticated');
      
      const response = await apiClient.post<{
        success: boolean;
        code?: ReferralCodeInfo;
        message?: string;
        error?: string;
      }>(
        '/api/referral/codes/create',
        {
          code,
          description,
          expiresAt: expiresAt?.toISOString(),
        },
        { token }
      );
      
      if (response.error || response.data?.error) {
        throw new Error(response.error || response.data?.error || '创建推荐码失败');
      }
      
      if (!response.data || !response.data.code) {
        throw new Error('创建推荐码失败，未返回推荐码信息');
      }
      
      logger.info('Referral code created successfully');
      return response.data as { success: boolean; code: ReferralCodeInfo; message: string };
    } catch (error: any) {
      logger.error('创建推荐码异常:', error);
      throw error;
    }
  }

  /**
   * 更新推荐码信息
   */
  async updateReferralCode(
    codeId: string,
    data: {
      description?: string;
      isActive?: boolean;
      bonusPerUse?: number;
      expiresAt?: Date | null;
    }
  ): Promise<ReferralCodeInfo> {
    logger.info(`Updating referral code: ${codeId}`, data);
    try {
      const token = authApi.getToken();
      if (!token) throw new Error('Not authenticated');
      
      // 根据不同的更新类型调用不同的API
      let updatedCode: any = null;

      // 更新赠金额度
      if (data.bonusPerUse !== undefined) {
        const response = await apiClient.put<{
          code?: ReferralCodeInfo;
          error?: string;
        }>(
          `/api/referral/codes/${codeId}/bonus-per-use`,
          { bonusPerUse: data.bonusPerUse },
          { token }
        );
        
        if (response.error || response.data?.error) {
          throw new Error(response.error || response.data?.error || '更新赠金额度失败');
        }
        
        updatedCode = response.data?.code;
      }

      // 更新描述
      if (data.description !== undefined) {
        const response = await apiClient.put<{
          code?: ReferralCodeInfo;
          error?: string;
        }>(
          `/api/referral/codes/${codeId}/description`,
          { description: data.description },
          { token }
        );
        
        if (response.error || response.data?.error) {
          throw new Error(response.error || response.data?.error || '更新描述失败');
        }
        
        updatedCode = response.data?.code;
      }

      // 切换状态
      if (data.isActive !== undefined) {
        const response = await apiClient.put<{
          code?: ReferralCodeInfo;
          error?: string;
        }>(
          `/api/referral/codes/${codeId}/toggle`,
          {},
          { token }
        );
        
        if (response.error || response.data?.error) {
          throw new Error(response.error || response.data?.error || '切换状态失败');
        }
        
        updatedCode = response.data?.code;
      }

      if (!updatedCode) {
        throw new Error('更新推荐码失败，未返回推荐码信息');
      }
      
      logger.info('Referral code updated successfully');
      return updatedCode;
    } catch (error: any) {
      logger.error('更新推荐码异常:', error);
      throw error;
    }
  }

  /**
   * 删除推荐码
   */
  async deleteReferralCode(codeId: string): Promise<{ success: boolean; message: string }> {
    logger.info(`Deleting referral code: ${codeId}`);
    try {
      const token = authApi.getToken();
      if (!token) throw new Error('Not authenticated');
      
      const response = await apiClient.delete<{
        success: boolean;
        message?: string;
        error?: string;
      }>(`/api/referral/codes/${codeId}`, { token });
      
      if (response.error || response.data?.error) {
        throw new Error(response.error || response.data?.error || '删除推荐码失败');
      }
      
      logger.info('Referral code deleted successfully');
      return response.data as { success: boolean; message: string };
    } catch (error: any) {
      logger.error('删除推荐码异常:', error);
      throw error;
    }
  }

  /**
   * 启用/禁用推荐码
   */
  async toggleReferralCode(codeId: string): Promise<{
    success: boolean;
    code: ReferralCodeInfo;
    message: string;
  }> {
    logger.info(`Toggling referral code: ${codeId}`);
    try {
      const token = authApi.getToken();
      if (!token) throw new Error('Not authenticated');
      
      const response = await apiClient.put<{
        success: boolean;
        code?: ReferralCodeInfo;
        message?: string;
        error?: string;
      }>(`/api/referral/codes/${codeId}/toggle`, {}, { token });
      
      if (response.error || response.data?.error) {
        throw new Error(response.error || response.data?.error || '操作失败');
      }
      
      if (!response.data || !response.data.code) {
        throw new Error('操作失败，未返回推荐码信息');
      }
      
      logger.info('Referral code toggled successfully');
      return response.data as { success: boolean; code: ReferralCodeInfo; message: string };
    } catch (error: any) {
      logger.error('切换推荐码状态异常:', error);
      throw error;
    }
  }

  /**
   * 更新推荐码描述
   */
  async updateReferralCodeDescription(
    codeId: string,
    description: string
  ): Promise<{ success: boolean; code: ReferralCodeInfo; message: string }> {
    logger.info(`Updating referral code description: ${codeId}`);
    try {
      const token = authApi.getToken();
      if (!token) throw new Error('Not authenticated');
      
      const response = await apiClient.put<{
        success: boolean;
        code?: ReferralCodeInfo;
        message?: string;
        error?: string;
      }>(
        `/api/referral/codes/${codeId}/description`,
        { description },
        { token }
      );
      
      if (response.error || response.data?.error) {
        throw new Error(response.error || response.data?.error || '更新失败');
      }
      
      if (!response.data || !response.data.code) {
        throw new Error('更新失败，未返回推荐码信息');
      }
      
      logger.info('Referral code description updated successfully');
      return response.data as { success: boolean; code: ReferralCodeInfo; message: string };
    } catch (error: any) {
      logger.error('更新推荐码描述异常:', error);
      throw error;
    }
  }

  // ========== 赠金池管理方法（新）==========

  /**
   * 向推荐码赠金池充值
   */
  async depositToBonusPool(
    codeId: string,
    amount: number
  ): Promise<{ success: boolean; transaction: BonusTransactionInfo; message: string }> {
    logger.info(`Depositing ${amount} to bonus pool: ${codeId}`);
    try {
      const token = authApi.getToken();
      if (!token) throw new Error('Not authenticated');
      
      const response = await apiClient.post<{
        success: boolean;
        transaction?: BonusTransactionInfo;
        message?: string;
        error?: string;
      }>(
        `/api/referral/codes/${codeId}/deposit`,
        { amount },
        { token }
      );
      
      if (response.error || response.data?.error) {
        throw new Error(response.error || response.data?.error || '充值失败');
      }
      
      if (!response.data || !response.data.transaction) {
        throw new Error('充值失败，未返回交易信息');
      }
      
      logger.info('Bonus pool deposit successful');
      return response.data as { success: boolean; transaction: BonusTransactionInfo; message: string };
    } catch (error: any) {
      logger.error('充值赠金池异常:', error);
      throw error;
    }
  }

  /**
   * 平台赠金（管理员操作）
   */
  async platformGrantBonus(
    codeId: string,
    amount: number,
    description?: string
  ): Promise<{ success: boolean; transaction: BonusTransactionInfo; message: string }> {
    logger.info(`Platform granting ${amount} to code: ${codeId}`);
    try {
      const token = authApi.getToken();
      if (!token) throw new Error('Not authenticated');
      
      const response = await apiClient.post<{
        success: boolean;
        transaction?: BonusTransactionInfo;
        message?: string;
        error?: string;
      }>(
        `/api/referral/codes/${codeId}/platform-grant`,
        { amount, description },
        { token }
      );
      
      if (response.error || response.data?.error) {
        throw new Error(response.error || response.data?.error || '平台赠金失败');
      }
      
      if (!response.data || !response.data.transaction) {
        throw new Error('平台赠金失败，未返回交易信息');
      }
      
      logger.info('Platform bonus granted successfully');
      return response.data as { success: boolean; transaction: BonusTransactionInfo; message: string };
    } catch (error: any) {
      logger.error('平台赠金异常:', error);
      throw error;
    }
  }

  /**
   * 设置每次使用的赠金额度
   */
  async setBonusPerUse(
    codeId: string,
    bonusPerUse: number
  ): Promise<{ success: boolean; code: ReferralCodeInfo; message: string }> {
    logger.info(`Setting bonus per use to ${bonusPerUse} for code: ${codeId}`);
    try {
      const token = authApi.getToken();
      if (!token) throw new Error('Not authenticated');
      
      const response = await apiClient.put<{
        success: boolean;
        code?: ReferralCodeInfo;
        message?: string;
        error?: string;
      }>(
        `/api/referral/codes/${codeId}/bonus-per-use`,
        { bonusPerUse },
        { token }
      );
      
      if (response.error || response.data?.error) {
        throw new Error(response.error || response.data?.error || '设置失败');
      }
      
      if (!response.data || !response.data.code) {
        throw new Error('设置失败，未返回推荐码信息');
      }
      
      logger.info('Bonus per use set successfully');
      return response.data as { success: boolean; code: ReferralCodeInfo; message: string };
    } catch (error: any) {
      logger.error('设置赠金额度异常:', error);
      throw error;
    }
  }

  /**
   * 查询推荐码赠金池状态
   */
  async getBonusPoolStatus(codeId: string): Promise<BonusPoolStatus | null> {
    logger.info(`Fetching bonus pool status for code: ${codeId}`);
    try {
      const token = authApi.getToken();
      if (!token) throw new Error('Not authenticated');
      
      const response = await apiClient.get<{ status?: BonusPoolStatus }>(
        `/api/referral/codes/${codeId}/bonus-pool`,
        { token }
      );
      
      const status = response.data?.status || null;
      logger.info('Bonus pool status fetched');
      return status;
    } catch (error: any) {
      logger.error('获取赠金池状态异常:', error);
      return null;
    }
  }

  /**
   * 查询推荐码赠金交易记录
   */
  async getBonusTransactions(codeId: string): Promise<BonusTransactionInfo[]> {
    logger.info(`Fetching bonus transactions for code: ${codeId}`);
    try {
      const token = authApi.getToken();
      if (!token) throw new Error('Not authenticated');
      
      const response = await apiClient.get<{ transactions?: BonusTransactionInfo[] }>(
        `/api/referral/codes/${codeId}/bonus-transactions`,
        { token }
      );
      
      const transactions = response.data?.transactions || [];
      logger.info(`Fetched ${transactions.length} bonus transactions`);
      return transactions;
    } catch (error: any) {
      logger.error('获取赠金交易记录异常:', error);
      return [];
    }
  }
}

export const referralApi = new ReferralApiService();

